import { ref } from 'vue';

import { crossbeamApi } from '@/api';
import {
  AWS_PARTNER_CENTRAL_DATA_SOURCE_TYPE,
  DataSourceFriendlyName,
} from '@/constants/data_sources';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { useFeatureFlagStore } from '@/stores/FeatureFlagStore';
import { usePartnersStore } from '@/stores/PartnersStore';
import type { Feed } from '@/types/feeds';
import type { Source } from '@/types/sources';

const AWS_PARTNER_CENTRAL_ORG_NAME =
  DataSourceFriendlyName[AWS_PARTNER_CENTRAL_DATA_SOURCE_TYPE];

// AWS Partner Central feed/source live on a per-customer offline-partner org,
// not in the customer's own FeedsStore/SourcesStore. Kept separate so they only
// surface in the data-sources UI, never in own-org consumers (population
// builder, reports, onboarding counts).
const awsFeeds = ref<Feed[]>([]);
const awsSources = ref<Source[]>([]);

/** The AWS Partner Central offline-partner org uuid, or null when the flag is
 * off or no such partner exists. Awaits the FF and partners stores. */
async function awsPartnerCentralOrgUuid(): Promise<string | null> {
  const featureFlagStore = useFeatureFlagStore();
  await featureFlagStore.readySync;
  if (!featureFlagStore.hasFeatureFlag(FEATURE_FLAG.AWS_PARTNER_CENTRAL)) {
    return null;
  }

  const partnersStore = usePartnersStore();
  await partnersStore.readySync;
  const awsPartner = partnersStore.offlinePartners.find(
    (partner) => partner.name === AWS_PARTNER_CENTRAL_ORG_NAME,
  );
  return awsPartner?.uuid ?? null;
}

export default function useAwsPartnerCentral() {
  /** Fetch the AWS feed/source via the v0.2 designated-org endpoints into the
   * shared refs; clears them when the flag is off or no partner exists. */
  async function refreshAwsPartnerCentral(): Promise<void> {
    const uuid = await awsPartnerCentralOrgUuid();
    if (!uuid) {
      awsFeeds.value = [];
      awsSources.value = [];
      return;
    }
    const [feedsResp, sourcesResp] = await Promise.all([
      crossbeamApi.GET('/v0.2/feeds', {
        params: { query: { offline_partner_org_uuid: uuid } },
      }),
      crossbeamApi.GET('/v0.2/sources', {
        params: {
          query: { offline_partner_org_uuid: uuid, include_deleted: true },
        },
      }),
    ]);
    // On failure, throw (like feedsStore.refreshFeed) so sync/delete polling
    // stops and surfaces the error instead of looping forever — but do NOT
    // assign here, so the cached feed/source is left intact rather than being
    // wiped to [] (which would make a connected AWS source vanish from the UI).
    // A network rejection propagates from the await above before any assignment.
    if (feedsResp.error || sourcesResp.error) {
      throw feedsResp.error ?? sourcesResp.error;
    }
    awsFeeds.value = (feedsResp.data?.items ?? []) as Feed[];
    awsSources.value = (sourcesResp.data?.items ?? []) as Source[];
  }

  const getAwsFeedById = (id: number): Feed | undefined =>
    awsFeeds.value.find((feed) => feed.id === id);

  const getAwsSourceById = (id: number): Source | undefined =>
    awsSources.value.find((source) => source.id === id);

  const getAwsSourcesByFeedId = (feedId: number): Source[] =>
    awsSources.value.filter((source) => source.feed_id === feedId);

  return {
    awsFeeds,
    awsSources,
    refreshAwsPartnerCentral,
    awsPartnerCentralOrgUuid,
    getAwsFeedById,
    getAwsSourceById,
    getAwsSourcesByFeedId,
  };
}
